<template>
  <section class="mb-card" data-test="ti-member-bars-card">
    <header class="mb-head">
      <div>
        <h3 class="mb-title">{{ title }}</h3>
        <p class="mb-sub">{{ subtitle }}</p>
      </div>
      <button
        type="button"
        class="mb-ghost"
        data-test="ti-members-table-toggle"
        :aria-expanded="showTable ? 'true' : 'false'"
        aria-label="Show or hide the data table"
        @click="showTable = !showTable"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="1.9" fill="currentColor" /><circle cx="12" cy="12" r="1.9" fill="currentColor" /><circle cx="19" cy="12" r="1.9" fill="currentColor" /></svg>
      </button>
    </header>

    <ul class="mb-legend" aria-label="Outcome colours">
      <li v-for="o in legend" :key="o.key"><i :style="{ background: o.color }" aria-hidden="true"></i>{{ o.label }}</li>
    </ul>

    <div class="mb-colhead" aria-hidden="true">
      <span></span><span>Team member</span><span></span><span class="mb-r">Total</span><span class="mb-r">Share</span>
    </div>

    <ul class="mb-rows" data-test="ti-member-rows">
      <li v-for="(r, i) in list.rows" :key="`${r.name}-${i}`" class="mb-row" data-test="ti-member-row">
        <span class="mb-avatar" aria-hidden="true">{{ initialsOf(r.name) }}</span>
        <span class="mb-name">{{ r.name }}</span>
        <div class="mb-barwrap">
          <div class="mb-bar" :style="{ width: r.barPercent + '%' }">
            <i
              v-for="s in r.segments"
              :key="s.label"
              class="mb-seg"
              :style="{ flex: `${s.count} 1 0`, background: s.color }"
              :title="`${r.name} — ${s.label}: ${s.count} (${s.percent}%)`"
            ></i>
          </div>
        </div>
        <span class="mb-total mb-r">{{ r.total }}</span>
        <span class="mb-share mb-r">{{ r.share }}%</span>
      </li>
    </ul>

    <div v-if="showTable" class="mb-table" data-test="ti-members-table">
      <table>
        <thead>
          <tr>
            <th scope="col">Team member</th>
            <th v-for="o in legend" :key="o.key" scope="col">{{ o.label }}</th>
            <th scope="col">Total</th>
            <th scope="col">Share</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in list.rows" :key="`t-${r.name}-${i}`">
            <th scope="row">{{ r.name }}</th>
            <td v-for="o in legend" :key="o.key">{{ countFor(r, o.label) }}</td>
            <td>{{ r.total }}</td>
            <td>{{ r.share }}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { buildOutcomeModel, buildMemberRows, initials } from './outcomeModel.js';

/**
 * By team member (Team Insights redesign, "Refined"): one list, each person's bar split by outcome, every bar on ONE
 * shared scale (the busiest person's bar is full width), with their share of the team. It is handed the same per-member
 * chartData the classic pies draw from, plus the TEAM chartData so each outcome keeps the colour it has on the team card.
 */
export default {
  name: 'MemberBarsCard',
  props: {
    // [{ name, chartData }] — patientBalancesDataDailyByEmp
    members: { type: Array, default: () => [] },
    // the team chartData — patientBalancesDataDaily.chartData
    teamChartData: { type: Object, default: null },
    title: { type: String, default: 'By team member' },
    subtitle: { type: String, default: 'Follow-ups today by outcome, one shared scale' },
  },
  data() {
    return { showTable: false };
  },
  computed: {
    teamModel() {
      return buildOutcomeModel(this.teamChartData);
    },
    list() {
      return buildMemberRows(this.members, this.teamModel);
    },
    // Only the outcomes somebody actually logged, so the legend never lists a colour that no bar shows.
    legend() {
      const used = {};
      this.list.rows.forEach(r => r.segments.forEach((s) => { used[s.label] = true; }));
      return this.teamModel.outcomes.filter(o => used[o.label]);
    },
  },
  methods: {
    initialsOf(name) {
      return initials(name);
    },
    countFor(row, label) {
      const seg = row.segments.find(s => s.label === label);
      return seg ? seg.count : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.mb-card {
  @include fu-font-base;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 28px;
  padding: 24px;
  box-shadow: $fu-shadow-sm;
  min-width: 0;
}

.mb-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.mb-title {
  @include fu-font-display;
  margin: 0;
  font-size: 20px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
}

.mb-sub {
  margin: 2px 0 0;
  font-size: 13.5px;
  color: $fu-text-muted;
}

.mb-ghost {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-text-muted;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover { color: $fu-text; }
}

.mb-legend {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12.5px;
  color: $fu-text-strong;

  li { display: inline-flex; align-items: center; gap: 6px; }
  i { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
}

.mb-colhead,
.mb-row {
  display: grid;
  grid-template-columns: 32px 120px minmax(0, 1fr) 34px 44px;
  align-items: center;
  gap: 12px;
}

.mb-colhead {
  margin-top: 16px;
  font-size: 11.5px;
  color: $fu-text-muted;
}

.mb-r { text-align: right; }

.mb-rows {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 14px;
}

.mb-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 11.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-strong;
  background: $fu-surface-sunken;
  border: 1px solid $fu-border;
}

.mb-name {
  min-width: 0;
  font-weight: $fu-fw-medium;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.mb-bar {
  display: flex;
  gap: 2px;
  height: 16px;
}

.mb-seg {
  display: block;
  height: 100%;
  min-width: 3px;

  &:first-child { border-radius: 4px 0 0 4px; }
  &:last-child { border-radius: 0 4px 4px 0; }
  &:only-child { border-radius: 4px; }
}

.mb-total {
  @include fu-tabular;
  font-weight: $fu-fw-semibold;
}

.mb-share {
  @include fu-tabular;
  font-size: 12px;
  color: $fu-text-muted;
}

// Narrow card: the bar drops under the name, full width.
@media (max-width: 560px) {
  .mb-colhead { display: none; }

  .mb-row {
    grid-template-columns: 32px minmax(0, 1fr) 34px 44px;
  }

  .mb-barwrap {
    grid-column: 2 / 5;
    grid-row: 2;
  }
}

.mb-table {
  margin-top: 16px;
  overflow-x: auto;

  table {
    @include fu-tabular;
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
  }

  th, td {
    padding: 6px 8px;
    border-bottom: 1px solid $fu-border;
    text-align: right;
  }

  th:first-child, td:first-child { text-align: left; white-space: nowrap; }
  thead th { color: $fu-text-muted; font-weight: $fu-fw-medium; vertical-align: bottom; }
  tbody th { font-weight: $fu-fw-regular; color: $fu-text-strong; }
}
</style>
